import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, MapPin, GraduationCap, DollarSign } from 'lucide-react';
import { universitiesAPI, countriesAPI } from '@/lib/api';

const UniversitiesPage = () => {
  const [universities, setUniversities] = useState([]);
  const [countries, setCountries] = useState([]);
  const [filteredUniversities, setFilteredUniversities] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [universitiesRes, countriesRes] = await Promise.all([
          universitiesAPI.getAll(),
          countriesAPI.getAll(),
        ]);
        setUniversities(universitiesRes.data);
        setFilteredUniversities(universitiesRes.data);
        setCountries(countriesRes.data);
      } catch (error) {
        setError('خطا در بارگذاری اطلاعات. لطفاً دوباره تلاش کنید.');
        // Continue with empty data to allow page to render
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let filtered = universities;
    if (searchTerm) {
      filtered = filtered.filter((uni) =>
        uni.name_fa.includes(searchTerm) || uni.name_en.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    if (selectedCountry) {
      filtered = filtered.filter((uni) => uni.country_id === selectedCountry);
    }
    setFilteredUniversities(filtered);
  }, [searchTerm, selectedCountry, universities]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><div className="text-[#D4AF37]">در حال بارگذاری...</div></div>;
  }

  return (
    <div className="min-h-screen py-12" data-testid="universities-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl font-light text-white mb-4" data-testid="page-title">دانشگاه‌های معتبر جهان</h1>
          <p className="text-zinc-400">بهترین دانشگاه‌ها برای تحصیل در خارج</p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="max-w-4xl mx-auto mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-center">
            {error}
          </div>
        )}

        {/* Filters */}
        <div className="max-w-4xl mx-auto mb-12 space-y-4">
          <div className="relative">
            <Search className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-500" size={20} />
            <input
              type="text"
              placeholder="جستجوی دانشگاه..."
              data-testid="search-input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-full px-12 py-4 text-white placeholder:text-zinc-500 focus:border-[#D4AF37] focus:outline-none focus:ring-1 focus:ring-[#D4AF37]"
            />
          </div>
          <select
            value={selectedCountry}
            onChange={(e) => setSelectedCountry(e.target.value)}
            data-testid="country-filter"
            className="w-full bg-white/5 border border-white/10 rounded-full px-6 py-4 text-white focus:border-[#D4AF37] focus:outline-none focus:ring-1 focus:ring-[#D4AF37]"
          >
            <option value="">همه کشورها</option>
            {countries.map((country) => (
              <option key={country.id} value={country.id}>{country.name_fa}</option>
            ))}
          </select>
        </div>

        {/* Universities Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredUniversities.map((university) => (
            <Link
              key={university.id}
              to={`/universities/${university.id}`}
              data-testid={`university-card-${university.id}`}
              className="group glass-effect border border-white/10 rounded-2xl overflow-hidden hover:border-[#D4AF37]/30 transition-all hover:-translate-y-1 gold-glow-hover"
            >
              <img
                src={university.image_url}
                alt={university.name_fa}
                className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="p-6">
                <div className="flex items-center gap-2 text-xs text-[#D4AF37] mb-2">
                  <GraduationCap size={14} />
                  <span>{university.rank}</span>
                </div>
                <h3 className="text-lg font-medium text-white mb-2 line-clamp-2">{university.name_fa}</h3>
                <div className="flex items-center gap-2 text-sm text-zinc-400 mb-3">
                  <MapPin size={14} />
                  <span>{university.city_fa}</span>
                </div>
                <div className="flex items-center justify-between pt-3 border-t border-white/10">
                  <span className="text-xs text-zinc-500">{university.university_type}</span>
                  <span className="text-sm text-[#D4AF37] font-medium">{university.tuition_range}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {filteredUniversities.length === 0 && (
          <div className="text-center py-12">
            <p className="text-zinc-400">دانشگاهی یافت نشد</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default UniversitiesPage;